import type { ColumnKind, DealRow } from './types'

/** A loan amount, which is dollars — the SAME rule the server aggregates and
 *  exports with (`amount_dollars` in app/services/dashboards/common.py), which
 *  keeps a row's cell consistent with the total above it. Both used to read
 *  anything under 10,000 as millions, so a $45 entry showed as $45.0M here and
 *  $45 on the Pipeline (stress test CMP-08). */
export function amountDollars(value: number | null | undefined): number | null {
  if (value === null || value === undefined) return null
  return value
}

export function money(value: number | null | undefined, decimals = 1): string {
  const dollars = amountDollars(value)
  if (dollars === null) return '—'
  if (Math.abs(dollars) >= 1_000_000_000) return `$${(dollars / 1_000_000_000).toFixed(decimals)}B`
  if (Math.abs(dollars) >= 1_000_000) return `$${(dollars / 1_000_000).toFixed(decimals)}M`
  if (Math.abs(dollars) >= 1_000) return `$${(dollars / 1_000).toFixed(0)}K`
  return `$${dollars.toFixed(0)}`
}

/** Totals from the API are already in dollars — do not re-normalize them. */
export function moneyExact(dollars: number | null | undefined, decimals = 1): string {
  if (dollars === null || dollars === undefined) return '—'
  if (Math.abs(dollars) >= 1_000_000_000) return `$${(dollars / 1_000_000_000).toFixed(decimals)}B`
  if (Math.abs(dollars) >= 1_000_000) return `$${(dollars / 1_000_000).toFixed(decimals)}M`
  if (Math.abs(dollars) >= 1_000) return `$${(dollars / 1_000).toFixed(0)}K`
  return `$${dollars.toFixed(0)}`
}

export function count(value: number | null | undefined): string {
  return value === null || value === undefined ? '—' : value.toLocaleString('en-US')
}

// Ratio-scale (0.65, not 65) like every other pct() in this app — see
// pipeline-intelligence/helpers.ts for the fixed pair and why.
export function pct(value: number | null | undefined, decimals = 1): string {
  return value === null || value === undefined ? '—' : `${(value * 100).toFixed(decimals)}%`
}

export function ratio(value: number | null | undefined): string {
  return value === null || value === undefined ? '—' : value.toFixed(2)
}

export function shortDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  const parsed = new Date(iso)
  if (Number.isNaN(parsed.getTime())) return iso
  return parsed.toISOString().slice(0, 10)
}

export function cellText(row: DealRow, key: string, kind: ColumnKind): string {
  const value = (row as unknown as Record<string, unknown>)[key]
  switch (kind) {
    case 'currency':
      return money(value as number | null)
    case 'percent':
      return pct(value as number | null)
    case 'ratio':
      return ratio(value as number | null)
    case 'number':
      return count(value as number | null)
    case 'date':
      return shortDate(value as string | null)
    default:
      return value === null || value === undefined || value === '' ? '—' : String(value)
  }
}

export const NUMERIC_KINDS: ReadonlySet<ColumnKind> = new Set([
  'currency',
  'percent',
  'ratio',
  'number',
])

export function columnWidth(key: string, kind: ColumnKind): string {
  if (key === 'property_name') return 'minmax(200px, 1.6fr)'
  if (key === 'dead_reason') return 'minmax(180px, 1.2fr)'
  if (key === 'location' || key === 'sponsor' || key === 'borrower') return 'minmax(150px, 1.1fr)'
  if (key === 'investor' || key === 'capital_program' || key === 'originator') {
    return 'minmax(140px, 1fr)'
  }
  if (kind === 'stage') return '150px'
  if (kind === 'status') return '90px'
  if (kind === 'date') return '105px'
  if (NUMERIC_KINDS.has(kind)) return '105px'
  return 'minmax(120px, 0.9fr)'
}

/** Human label for a field name coming out of `missing_field_names`. */
export function fieldLabel(field: string): string {
  return field
    .split('_')
    .map((part) => (part === 'ltv' ? 'LTV' : part === 'dscr' ? 'DSCR' : part))
    .map((part) => (part.length <= 4 && part === part.toUpperCase() ? part : part.charAt(0).toUpperCase() + part.slice(1)))
    .join(' ')
}
